// ---------------------------------------------------------
// Logboek24 — Beheerpagina: Auditbeleid instellen (JWT + automatisch tenantdetectie)
// ---------------------------------------------------------
import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import api from "../services/api";

function BeheerAuditbeleid() {
  const [niveau, setNiveau] = useState("basis");
  const [loading, setLoading] = useState(true);
  const [fout, setFout] = useState(null);
  const [huidig, setHuidig] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  // 🔹 Tenant & gebruiker bepalen via JWT
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserInfo(decoded);
      } catch (err) {
        console.warn("⚠️ JWT kon niet worden gelezen:", err);
      }
    }
  }, []);

  const tenantId = userInfo?.tenantId || "demo";
  const userNaam = userInfo?.naam || "beheerder-demo";

  // ---------------------------------------------------------
  // 📥 Ophalen huidig beleid
  // ---------------------------------------------------------
  useEffect(() => {
    const fetchBeleid = async () => {
      try {
        const res = await api.get(`/auditsettings/${tenantId}`);
        const data = res.data;
        if (data?.niveau) {
          setNiveau(data.niveau);
          setHuidig(data.niveau);
        }
      } catch (err) {
        setFout(err?.response?.data?.message || err.message || "Kon huidig auditbeleid niet ophalen");
      } finally {
        setLoading(false);
      }
    };
    fetchBeleid();
  }, [tenantId]);

  // ---------------------------------------------------------
  // 💾 Opslaan van gekozen auditniveau
  // ---------------------------------------------------------
  const handleOpslaan = async () => {
    try {
      const res = await api.put(`/auditsettings/${tenantId}`, {
        niveau,
        gewijzigdDoor: userNaam,
      });
      const data = res.data;

      if (data?.updated) {
        alert(
          `✅ Minimumniveau opgeslagen: ${data.updated.niveau.toUpperCase()}\n\n` +
            `De software past automatisch een hoger niveau toe als de actie dit vereist.`
        );
        setHuidig(data.updated.niveau);
      } else {
        alert("❌ Fout bij opslaan: " + (data.message || "Onbekende fout"));
      }
    } catch (err) {
      console.error("❌ Auditbeleid opslaan mislukt:", err);
      alert(`❌ ${err?.response?.data?.message || "Netwerkfout of server niet bereikbaar."}`);
    }
  };

  // ---------------------------------------------------------
  // 🧩 UI
  // ---------------------------------------------------------
  if (loading) return <div className="p-6 text-gray-600">⏳ Auditbeleid laden...</div>;
  if (fout) return <div className="p-6 text-red-600">❌ {fout}</div>;

  return (
    <div className="max-w-3xl mx-auto bg-white shadow-md rounded-xl p-6 mt-10">
      <h1 className="text-2xl font-bold mb-4">⚙️ Auditbeleid instellen</h1>
      <p className="text-gray-600 mb-6">
        Stel hier het <strong>minimale auditniveau</strong> in voor deze tenant.
        De software bepaalt bij elke actie automatisch of een hoger niveau vereist is.
      </p>

      {/* 🔘 Keuze auditniveaus */}
      <div className="space-y-3 mb-6">
        {[
          {
            key: "basis",
            label: "Basis",
            desc: "alleen kernacties worden gelogd (aanmaken / verwijderen).",
          },
          {
            key: "versterkt",
            label: "Versterkt",
            desc: "ook aanpassingen, fotowijzigingen en PDF-generaties worden vastgelegd.",
          },
          {
            key: "forensisch",
            label: "Forensisch",
            desc: "elk detail, inclusief IP, device, en veldwijzigingen.",
          },
        ].map((opt) => (
          <label key={opt.key} className="flex items-start gap-3">
            <input
              type="radio"
              name="niveau"
              value={opt.key}
              checked={niveau === opt.key}
              onChange={() => setNiveau(opt.key)}
            />
            <span>
              <strong>{opt.label}</strong> — {opt.desc}
            </span>
          </label>
        ))}
      </div>

      {/* 💾 Opslaan-knop + status */}
      <div className="border-t pt-4">
        <button
          onClick={handleOpslaan}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
        >
          💾 Opslaan als minimumniveau
        </button>

        <div className="mt-6 text-sm text-gray-600 leading-relaxed">
          Huidig ingesteld niveau:{" "}
          <strong className="uppercase text-blue-700">{huidig || "onbekend"}</strong>
          <br />
          Dit niveau geldt als <strong>minimaal</strong> beleid voor tenant{" "}
          <span className="italic">{tenantId}</span>.  
          De software bepaalt automatisch of een strenger niveau (zoals
          <em> forensisch</em>) vereist is.
        </div>
      </div>
    </div>
  );
}

export default BeheerAuditbeleid;
